import { useMemo } from 'react'

import { TreeItemIndex, TreeItem } from 'react-complex-tree'

import { SystemSpaceSlug } from '@bettermode/common/space'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'

import { UTILITY_SPACE_SLUGS } from '../constants.js'
import {
  createSpaceTreeItem,
  spaceTreeIndex,
} from '../tree-items/createSpaceTreeItem.js'
import {
  createUtilitySpacesRootTreeItem,
  UTILITY_SPACES_INDEX,
} from '../tree-items/createUtilitySpacesRootTreeItem.js'
import { TreeItemData, TreeItems } from '../types.js'

import { useLimitTreeItems } from './useLimitTreeItems.js'

export function useUtilitySpacesTreeItems({ query }: { query?: string }) {
  const intl = useI18n()
  const { data: { systemSpaces } = {} } = useNetwork()

  const utilitySpaces = useMemo(
    () =>
      systemSpaces?.filter(space =>
        UTILITY_SPACE_SLUGS.includes(space.slug as SystemSpaceSlug),
      ),
    [systemSpaces],
  )

  const foundUtilitySpaces = useMemo(
    () =>
      query
        ? utilitySpaces.filter(space => {
            return space.name?.toLowerCase().includes(query.toLowerCase())
          })
        : utilitySpaces,
    [query, utilitySpaces],
  )

  const {
    items: limitedUtilitySpacesRootItem,
    children: utilitySpacesRootChildren,
  } = useLimitTreeItems(
    UTILITY_SPACES_INDEX,
    foundUtilitySpaces.map(space => spaceTreeIndex(space.id)),
  )

  const utilitySpacesRootItem = useMemo(() => {
    return {
      [UTILITY_SPACES_INDEX]: createUtilitySpacesRootTreeItem(intl, {
        children: utilitySpacesRootChildren,
      }),
    }
  }, [intl, utilitySpacesRootChildren])

  const treeItems: Record<
    TreeItemIndex,
    TreeItem<TreeItemData>
  > = useMemo((): TreeItems => {
    return foundUtilitySpaces.reduce((acc, space) => {
      return { ...acc, ...createSpaceTreeItem(space) }
    }, {})
  }, [foundUtilitySpaces])

  const utilitySpacesTreeItems = useMemo(() => {
    return {
      ...utilitySpacesRootItem,
      ...limitedUtilitySpacesRootItem,
      ...treeItems,
    }
  }, [utilitySpacesRootItem, limitedUtilitySpacesRootItem, treeItems])

  return {
    utilitySpacesTreeItems,
    utilitySpacesIndex: UTILITY_SPACES_INDEX,
  }
}
